// src/components/circles/smartRules.ts
//
// Client-side mirror of the canonical Contact filter whitelist
// (server/src/services/contactFilter/schema.ts): which fields a Smart Circle
// rule may use, the conditions each allows, and the kind of value each takes.
// The server is the authority — it re-validates everything and answers
// 400 INVALID_SMART_RULES with field-level errors — this file only drives the
// builder UI and the plain-language summary.

import type {
  ContactFilter,
  ContactFilterError,
  ContactFilterField,
  ContactFilterRule,
} from '../../types/api';

export type RuleValueKind =
  | 'tagList'
  | 'memberId'
  | 'statusList'
  | 'typeList'
  | 'companyId'
  | 'date'
  | 'days'
  | 'text'
  | 'none';

interface FieldMeta {
  label: string;
  ops: Array<{ op: string; label: string; kind: RuleValueKind }>;
}

export const RELATIONSHIP_STATUS_LABELS: Record<string, string> = {
  cold: 'Cold',
  contacted: 'Contacted',
  answered: 'Answered',
  follow_up: 'Follow-up',
  existing: 'Existing',
  customer: 'Customer',
  do_not_contact: 'Do not contact',
};

export const CONTACT_TYPE_LABELS: Record<string, string> = {
  lead: 'Lead',
  client: 'Client',
  vendor: 'Vendor',
  partner: 'Partner',
  team: 'Team',
  other: 'Other',
};

const DATE_OPS = [
  { op: 'withinDays', label: 'in the last … days', kind: 'days' as const },
  { op: 'notWithinDays', label: 'not in the last … days', kind: 'days' as const },
  { op: 'before', label: 'before', kind: 'date' as const },
  { op: 'onOrBefore', label: 'on or before', kind: 'date' as const },
  { op: 'onOrAfter', label: 'on or after', kind: 'date' as const },
];
const TEXT_OPS = [
  { op: 'equals', label: 'is', kind: 'text' as const },
  { op: 'contains', label: 'contains', kind: 'text' as const },
];

export const FIELD_META: Record<ContactFilterField, FieldMeta> = {
  tags: {
    label: 'Tags',
    ops: [
      { op: 'hasAll', label: 'include all of', kind: 'tagList' },
      { op: 'hasAny', label: 'include any of', kind: 'tagList' },
    ],
  },
  ownerUserId: {
    label: 'Owner',
    ops: [
      { op: 'is', label: 'is', kind: 'memberId' },
      { op: 'isNot', label: 'is not', kind: 'memberId' },
    ],
  },
  relationshipStatus: {
    label: 'Relationship status',
    ops: [
      { op: 'in', label: 'is any of', kind: 'statusList' },
      { op: 'notIn', label: 'is none of', kind: 'statusList' },
    ],
  },
  type: { label: 'Contact type', ops: [{ op: 'in', label: 'is any of', kind: 'typeList' }] },
  companyId: { label: 'Company', ops: [{ op: 'affiliatedWith', label: 'works with', kind: 'companyId' }] },
  hasEmail: {
    label: 'Email address',
    ops: [
      { op: 'isTrue', label: 'is on file', kind: 'none' },
      { op: 'isFalse', label: 'is missing', kind: 'none' },
    ],
  },
  hasPhone: {
    label: 'Phone number',
    ops: [
      { op: 'isTrue', label: 'is on file', kind: 'none' },
      { op: 'isFalse', label: 'is missing', kind: 'none' },
    ],
  },
  lastContactedAt: {
    label: 'Last contacted',
    ops: [
      ...DATE_OPS,
      { op: 'isEmpty', label: 'never', kind: 'none' },
      { op: 'isNotEmpty', label: 'at any time', kind: 'none' },
    ],
  },
  createdAt: { label: 'Created', ops: DATE_OPS },
  city: { label: 'City', ops: TEXT_OPS },
  state: { label: 'State', ops: TEXT_OPS },
  zip: { label: 'ZIP / postal code', ops: TEXT_OPS },
};

export const FIELD_ORDER: ContactFilterField[] = [
  'tags',
  'ownerUserId',
  'relationshipStatus',
  'type',
  'companyId',
  'lastContactedAt',
  'createdAt',
  'hasEmail',
  'hasPhone',
  'city',
  'state',
  'zip',
];

export function opMeta(field: ContactFilterField | '', op: string) {
  if (!field) return undefined;
  return FIELD_META[field]?.ops.find((o) => o.op === op);
}

/**
 * One editable rule row. `value` holds the INPUT state (e.g. the tag text
 * "vip, gold", the days as typed) — converted to the canonical value only
 * when building the filter.
 */
export interface SmartRuleDraft {
  key: string;
  field: ContactFilterField | '';
  op: string;
  value: string | string[];
}

let draftSeq = 0;
export function newDraft(): SmartRuleDraft {
  draftSeq += 1;
  return { key: `rule-${Date.now().toString(36)}-${draftSeq}`, field: '', op: '', value: '' };
}

/** Reset op/value when the field changes (the old value may not fit). */
export function withField(d: SmartRuleDraft, field: ContactFilterField | ''): SmartRuleDraft {
  const firstOp = field ? FIELD_META[field].ops[0] : undefined;
  return { ...d, field, op: firstOp?.op ?? '', value: firstOp && isListKind(firstOp.kind) ? [] : '' };
}

export function withOp(d: SmartRuleDraft, op: string): SmartRuleDraft {
  const prev = opMeta(d.field, d.op)?.kind;
  const next = opMeta(d.field, op)?.kind;
  return { ...d, op, value: prev === next ? d.value : next && isListKind(next) ? [] : '' };
}

function isListKind(k: RuleValueKind): boolean {
  return k === 'statusList' || k === 'typeList';
}

function splitTags(text: string): string[] {
  return text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

export function draftToRule(d: SmartRuleDraft): ContactFilterRule {
  const kind = opMeta(d.field, d.op)?.kind;
  const rule: ContactFilterRule = { field: d.field as ContactFilterField, op: d.op };
  switch (kind) {
    case 'none':
      return rule;
    case 'tagList':
      return { ...rule, value: splitTags(String(d.value ?? '')) };
    case 'days': {
      const n = Number(String(d.value).trim());
      return { ...rule, value: String(d.value).trim() === '' || !Number.isFinite(n) ? String(d.value) : n };
    }
    case 'statusList':
    case 'typeList':
      return { ...rule, value: Array.isArray(d.value) ? d.value : [] };
    default:
      return { ...rule, value: typeof d.value === 'string' ? d.value.trim() : d.value };
  }
}

export function draftsToFilter(drafts: SmartRuleDraft[]): ContactFilter {
  return { v: 1, all: drafts.map(draftToRule) };
}

export function filterToDrafts(filter: ContactFilter | null | undefined): SmartRuleDraft[] {
  if (!filter || !Array.isArray(filter.all)) return [];
  return filter.all.map((r) => {
    const kind = opMeta(r.field, r.op)?.kind;
    let value: string | string[] = '';
    if (kind === 'tagList') value = Array.isArray(r.value) ? (r.value as string[]).join(', ') : '';
    else if (kind === 'statusList' || kind === 'typeList') value = Array.isArray(r.value) ? (r.value as string[]) : [];
    else if (kind === 'days') value = r.value == null ? '' : String(r.value);
    else if (kind === 'date') value = typeof r.value === 'string' ? r.value.slice(0, 10) : '';
    else if (kind && kind !== 'none') value = typeof r.value === 'string' ? r.value : '';
    return { ...newDraft(), field: r.field, op: r.op, value };
  });
}

/**
 * Client-side completeness check — catches the empty states the user can see
 * at a glance. Everything else (ranges, references) is the server's call.
 * Returns errors keyed by draft index, in the server's path vocabulary.
 */
export function incompleteRuleErrors(drafts: SmartRuleDraft[]): ContactFilterError[] {
  const errors: ContactFilterError[] = [];
  if (drafts.length === 0) {
    errors.push({ path: 'all', code: 'NO_RULES', message: 'Add at least one rule.' });
  }
  drafts.forEach((d, i) => {
    if (!d.field) {
      errors.push({ path: `all[${i}].field`, code: 'UNKNOWN_FIELD', message: 'Choose what to match on.' });
      return;
    }
    const kind = opMeta(d.field, d.op)?.kind;
    if (!kind) {
      errors.push({ path: `all[${i}].op`, code: 'UNSUPPORTED_OPERATOR', message: 'Choose a condition.' });
      return;
    }
    if (kind === 'none') return;
    const empty = Array.isArray(d.value)
      ? d.value.length === 0
      : kind === 'tagList'
        ? splitTags(d.value).length === 0
        : String(d.value).trim() === '';
    if (empty) errors.push({ path: `all[${i}].value`, code: 'INVALID_VALUE', message: valuePrompt(kind) });
  });
  return errors;
}

function valuePrompt(kind: RuleValueKind): string {
  switch (kind) {
    case 'tagList':
      return 'Enter at least one tag.';
    case 'memberId':
      return 'Choose a person.';
    case 'companyId':
      return 'Choose a company.';
    case 'statusList':
    case 'typeList':
      return 'Choose at least one.';
    case 'date':
      return 'Choose a date.';
    case 'days':
      return 'Enter a number of days.';
    default:
      return 'Enter a value.';
  }
}

/** Group server/client errors by rule index ("all" → -1). */
export function errorsByRule(errors: ContactFilterError[] | undefined): Map<number, ContactFilterError[]> {
  const out = new Map<number, ContactFilterError[]>();
  for (const e of errors ?? []) {
    const m = /^all\[(\d+)\]/.exec(e.path);
    const idx = m ? Number(m[1]) : -1;
    out.set(idx, [...(out.get(idx) ?? []), e]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Submit helpers
// ---------------------------------------------------------------------------

export type SmartRulesSubmission =
  | { ok: true; rules?: ContactFilter }
  | { ok: false; errors: ContactFilterError[] };

/**
 * What a create/update body should carry for Smart Circle rules.
 *
 *   not a Smart Circle               → nothing.
 *   create, or switching TO smart    → the rules (must be complete).
 *   already smart, rules unchanged   → nothing (the server keeps them; a
 *                                      legacy Circle with no rules can still
 *                                      be renamed without adding any).
 *   already smart, rules changed     → the rules (must be complete).
 */
export function smartRulesForSubmit(
  type: string,
  drafts: SmartRuleDraft[] | undefined,
  existing?: { type: string; drafts: SmartRuleDraft[] },
): SmartRulesSubmission {
  if (type !== 'smart_segment') return { ok: true };
  const current = drafts ?? [];
  if (existing && existing.type === 'smart_segment') {
    const before = JSON.stringify(draftsToFilter(existing.drafts));
    if (JSON.stringify(draftsToFilter(current)) === before) return { ok: true };
  }
  const errors = incompleteRuleErrors(current);
  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, rules: draftsToFilter(current) };
}

/** Field-level errors from a refused save, if that is what `err` is. */
export function smartRuleErrorsFrom(err: unknown): ContactFilterError[] | null {
  const data = (err as { data?: { code?: string; errors?: unknown } })?.data;
  return data?.code === 'INVALID_SMART_RULES' && Array.isArray(data.errors)
    ? (data.errors as ContactFilterError[])
    : null;
}

// ---------------------------------------------------------------------------
// Plain-language summary
// ---------------------------------------------------------------------------

export interface SummaryLookups {
  memberName?: (userId: string) => string | undefined;
  companyName?: (companyId: string) => string | undefined;
}

function list(values: string[], labels?: Record<string, string>): string {
  const named = values.map((v) => labels?.[v] ?? v);
  if (named.length <= 1) return named.join('');
  return `${named.slice(0, -1).join(', ')} or ${named[named.length - 1]}`;
}

function formatDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** One rule as a sentence fragment, e.g. "Tags include any of vip or gold". */
export function describeRule(rule: ContactFilterRule, lookups: SummaryLookups = {}): string {
  const meta = FIELD_META[rule.field];
  if (!meta) return 'Unknown rule';
  const v = rule.value;
  const days = (n: unknown) => `${n} ${Number(n) === 1 ? 'day' : 'days'}`;
  switch (rule.field) {
    case 'tags': {
      const tags = (Array.isArray(v) ? (v as string[]) : []).map((t) => `“${t}”`);
      return rule.op === 'hasAll' ? `Tagged ${tags.join(' and ')}` : `Tagged ${list(tags)}`;
    }
    case 'ownerUserId': {
      const who = lookups.memberName?.(String(v)) ?? 'a workspace member';
      return rule.op === 'is' ? `Owned by ${who}` : `Not owned by ${who}`;
    }
    case 'relationshipStatus': {
      const s = list(Array.isArray(v) ? (v as string[]) : [], RELATIONSHIP_STATUS_LABELS);
      return rule.op === 'in' ? `Relationship status is ${s}` : `Relationship status is not ${s}`;
    }
    case 'type':
      return `Contact type is ${list(Array.isArray(v) ? (v as string[]) : [], CONTACT_TYPE_LABELS)}`;
    case 'companyId':
      return `Works with ${lookups.companyName?.(String(v)) ?? 'a company you can’t view'}`;
    case 'hasEmail':
      return rule.op === 'isTrue' ? 'Has an email address' : 'Has no email address';
    case 'hasPhone':
      return rule.op === 'isTrue' ? 'Has a phone number' : 'Has no phone number';
    case 'lastContactedAt':
    case 'createdAt': {
      const what = rule.field === 'lastContactedAt' ? 'Last contacted' : 'Created';
      switch (rule.op) {
        case 'withinDays':
          return `${what} in the last ${days(v)}`;
        case 'notWithinDays':
          return rule.field === 'lastContactedAt'
            ? `Not contacted in the last ${days(v)}`
            : `Created more than ${days(v)} ago`;
        case 'before':
          return `${what} before ${formatDate(String(v))}`;
        case 'onOrBefore':
          return `${what} on or before ${formatDate(String(v))}`;
        case 'onOrAfter':
          return `${what} on or after ${formatDate(String(v))}`;
        case 'isEmpty':
          return 'Never contacted';
        case 'isNotEmpty':
          return 'Contacted at least once';
      }
      return what;
    }
    case 'city':
    case 'state':
    case 'zip':
      return `${meta.label} ${rule.op === 'equals' ? 'is' : 'contains'} “${String(v)}”`;
  }
  return meta.label;
}
